import { AlertTriangle, Database, Link2, Minimize2 } from "lucide-react";
import { Container } from "@/components/ui/container";
import { Badge } from "@/components/ui/badge";

export function DataMinimization() {
  return (
    <section aria-labelledby="minimization-heading" className="bg-primary py-16 text-primary-foreground sm:py-24">
      <Container className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
        <div>
          <Badge variant="outline" className="border-white/30 bg-white/10 text-white">
            Data minimization by design
          </Badge>
          <h2 id="minimization-heading" className="mt-5 max-w-2xl text-3xl font-bold tracking-tight sm:text-4xl">
            Bring the privacy-management record, not your entire customer database.
          </h2>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/80">
            DataProtection.work is not a CRM, HRIS, customer database, data lake, file-sharing product or backup service. The product is designed around categories, summaries, references, case-level details and evidence links so you can document privacy work without copying unnecessary personal data into another system.
          </p>
        </div>

        <div className="grid gap-4">
          <div className="rounded-xl border border-white/20 bg-white/10 p-5">
            <div className="flex gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-brand-300" aria-hidden="true" />
              <div>
                <h3 className="font-semibold text-white">Do not upload bulk operational datasets.</h3>
                <p className="mt-1 text-sm leading-relaxed text-white/75">
                  Customer databases, CRM exports, employee databases, mailing lists, contact lists, database dumps and backup archives do not belong here merely because they contain personal information.
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-white/20 bg-white/10 p-5">
            <div className="flex gap-3">
              <Minimize2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-300" aria-hidden="true" />
              <div>
                <h3 className="font-semibold text-white">Use only what a case actually needs.</h3>
                <p className="mt-1 text-sm leading-relaxed text-white/75">
                  For a privacy request, incident, complaint, consent record or similar compliance-monitoring matter, enter only the minimum person-level information reasonably necessary to manage and document that matter.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex gap-3 rounded-xl border border-white/20 bg-white/10 p-4">
              <Link2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-300" aria-hidden="true" />
              <p className="text-sm leading-relaxed text-white/75">Prefer links to authoritative evidence in your existing systems.</p>
            </div>
            <div className="flex gap-3 rounded-xl border border-white/20 bg-white/10 p-4">
              <Database className="mt-0.5 h-5 w-5 shrink-0 text-brand-300" aria-hidden="true" />
              <p className="text-sm leading-relaxed text-white/75">Prefer references, categories, summaries and pseudonymous IDs over raw datasets.</p>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
